import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import "./PipesChart.css";
import { chartColors } from "../../config/constantsColor";

const PipesChart = ({ data, product }) => {
  const chartComponentRef = useRef(null);
  const [labelPositions, setLabelPositions] = useState([]);
  const prevDataRef = useRef(data);
  const [visibleBars, setVisibleBars] = useState(5); // Default number of visible bars

  const categories = data.map((item) => item.size);
  const passData = data.map((item) => item.genuine_count);
  const failData = data.map((item) => item.counterfeit_count);
  
  const calculateYield = (genuine, counterfeit) => {
    const total = genuine + counterfeit;
    if (total === 0) return "0%";
    return `${Math.round((genuine / total) * 100)}%`;
  };

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth <= 1290) {
        const chart = chartComponentRef.current?.chart;
        if (chart) {
          // Set zoom to show only 5 bars when screen is 1290px or smaller
          const dataLength = data.length;
          if (dataLength > visibleBars) {
            chart.xAxis[0].setExtremes(0, visibleBars - 1);
          }
        }
      }
    };   

    // Initial setup
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [data, visibleBars]);

  const options = {
    chart: {
      type: "column",
      height: 400,
      zoomType: "x",
      style: {
        fontFamily: "sans-serif",
      },
      events: {
        render() {
          const chart = this;
          const newPositions = chart.series[0].points.map((point) => {
            const { plotX, plotY } = point;
            return {
              left: chart.plotLeft + plotX + point.shapeArgs.width / 2,
              top: chart.plotTop + plotY - 30, // 30px above the bar
            };
          });

          // Only update if the positions have changed to avoid infinite loops
          if (JSON.stringify(newPositions) !== JSON.stringify(labelPositions)) {
            setLabelPositions(newPositions);
          }
        },
        load: function() {
          if (window.innerWidth <= 1290) {
            // Set initial zoom on load for smaller screens
            this.xAxis[0].setExtremes(0, visibleBars - 1);
          }
        },
      },
    },

    title: {
      text: null,
      align: "left",
      style: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#001323",
      },

    },
    xAxis: {
      scrollbar: {
        enabled: true,
      },
      events: {
        setExtremes: function () {
          setTimeout(() => {
            updateLabelPositions();
          }, 0);
        }
      },
      categories,
      title: {
        text: null,
        style: {
          fontSize: "18px",
          fontWeight: "500",
          color: "#001323",
        },
      },
      labels: {
        style: {
          color: "#001323",
          fontSize: "12px",
        },
      },
      lineColor: "transparent",
      tickLength: 0,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Scans",
        style: {
          fontSize: "18px",
          fontWeight: "500",
          color: "#001323",
        },
      },
      labels: {
        style: {
          color: "#666666",
        },
      },
      gridLineWidth: 0,
    },
    
    tooltip: {
      useHTML: true,
      formatter: function () {
        const index = this.point.index;
        const item = data[index];
        const total = item.genuine_count + item.counterfeit_count;
        return `
          <div style="background:white; border:1px solid #E0E0E0; padding:12px; border-radius:4px; font-size:14px; color:#001323">
            <div><span style="color: #005AA6">Total - ${total}</span></div>
            <div><span style="color: ${chartColors['Genuine']}">Pass - ${item.genuine_count}</span></div>
            <div><span style="color: ${chartColors['Counterfeit']}">Fail - ${item.counterfeit_count}</span></div>
          </div>
        `;
      },
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadiusTopLeft: "4px",
        borderRadiusTopRight: "4px",
        pointPadding: 0.2,
        groupPadding: 0.1,
      },
    },
    series: [
      {
        name: "Pass",
        data: passData,
        color: chartColors["Genuine"],
      },
      {
        name: "Fail",
        data: failData,
        color: chartColors["Counterfeit"],
      },
    ],
    credits: {
      enabled: false,
    },
  };
  const updateLabelPositions = () => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
  
    const newPositions = chart.series[0].points.map((point) => {
      const left = chart.plotLeft + point.plotX + (point.shapeArgs?.width || 0) / 2;
      const top = chart.plotTop + point.plotY - 30;
      return { left, top };
    });
  
    setLabelPositions(newPositions);
  };
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      updateLabelPositions();
  
      // Optional: reflow for resize issues
      chart.reflow();
    }
  }, []);
  
  // Effect to avoid infinite re-render
  useEffect(() => {
    if (JSON.stringify(data) !== JSON.stringify(prevDataRef.current)) {
      prevDataRef.current = data;
      const chart = chartComponentRef.current?.chart;
      if (chart) {
        chart.reflow(); // Ensure rendering positions are accurate on load
      }
    }
  }, [data]);

  return (
    <>
    <div className="pt-3">
    <div className="x-axis-label" style={{textAlign:"left",marginLeft:"36px"}}>{product}</div>
    <div className="pipes-chart-wrapper" style={{ position: "relative" }}>

      <div>
      {labelPositions.map((pos, index) => (
        
        <div
          key={index}
          className={`yield-tooltip tool${index + 1}`}
          style={{
            position: "absolute",
            transform: "translateX(-66%)",
            left: `${pos.left}px`,
            top:"1%",
            fontSize: "12px",
            backgroundColor: "#fff",
            padding: "4px 8px",
            borderRadius: "4px",
            whiteSpace: "nowrap",
            color: "#001323",
            zIndex:"999999"
          }}
        >
          {calculateYield(
            data[index]?.genuine_count,
            data[index]?.counterfeit_count
          )}{" "}
          Yield
        </div>
      
      ))}
        </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
      <div className="x-axis-label">Product Types</div>
    </div>
    </div>
    </>
  );
};

export default PipesChart;
